import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, SearchIcon } from 'lucide-react';
import { ChangeEvent, useRef, useState } from 'react';
import ResourceDeleteConfirmation from '../components/ResourceManager/ResourceDeleteConfirmation';
import { ResourceDocumentPreview } from '../components/ResourceManager/ResourceDocumentPreview';
import { ResourceModal } from '../components/ResourceManager/ResourceModal';
import { ResourceTable } from '../components/ResourceManager/ResourceTable';
import { useResourceApi } from '../services/api/useResource.api';
import { useUserApi } from '../services/api/useUser.api';
import { ApiResponse } from '../types/ApiResponse.type';
import { ApprovalRequestStatus } from '../types/enums/ApprovalRequestStatus.enum';
import { ResourceStatus } from '../types/enums/ResourceStatus.enum';
import { ResourceType } from '../types/enums/ResourceType.enum';
import { Resource } from '../types/Resources/Resource.type';
import { ResourceCreate } from '../types/Resources/ResourceCreate.type';
import { ResourceReview } from '../types/Resources/ResourceReview.type';
import { ResourceUpdate } from '../types/Resources/ResourceUpdate.type';

function ResourceManager() {
  const queryClient = useQueryClient();
  const { getUserInfo } = useUserApi();
  const {
    getResources,
    createResource,
    updateResource,
    getResourceById,
    reviewResource,
    toggleFavorite,
    deleteResource,
  } = useResourceApi();

  const [activeTab, setActiveTab] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [modalAction, setModalAction] = useState<'save' | 'approve' | 'decline'>('save');
  const [searchText, setSearchText] = useState('');
  const [sortBy] = useState('');
  const [query, setQuery] = useState('');
  const [fetchResourceId, setFetchResourceId] = useState<number | null>(null);
  const [fetchAction, setFetchAction] = useState<'preview' | 'download' | null>(null);
  const [editResourceId, setEditResourceId] = useState<number | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approve' | 'decline'>('approve');
  const [reviewComment, setReviewComment] = useState('');
  const [resourceToReview, setResourceToReview] = useState<number | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState<Resource | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: userInfo } = useQuery({
    queryKey: ['userInfo'],
    queryFn: getUserInfo,
  });

  const { data: approvalCountData } = useQuery({
    queryKey: ['resources', 'approvalCount'],
    queryFn: async () => {
      const response = await getResources({
        page: 1,
        per_page: 30,
        sort: '',
        query: '',
        status_id: ResourceStatus.DRAFT,
      });
      return response.meta.pagination?.total ?? 0;
    },
  });

  const approvalCount = approvalCountData ?? 0;

  const createResourceMutation = useMutation({
    mutationKey: ['createResource'],
    mutationFn: createResource,
    onSuccess: () => {
      setIsModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['resources'] });
      setSnackbarMessage('Resource created successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    },
    onError: (error) => {
      setSnackbarMessage(error?.message || 'Failed to create resource. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    },
  });

  const updateResourceMutation = useMutation({
    mutationKey: ['updateResource'],
    mutationFn: ({ resourceId, updateData }: { resourceId: number; updateData: Partial<ResourceUpdate> }) =>
      updateResource(resourceId, updateData),
    onSuccess: () => {
      setIsModalOpen(false);
      setEditResourceId(null);
      queryClient.invalidateQueries({ queryKey: ['resources'] });
      queryClient.invalidateQueries({ queryKey: ['resourceItem', editResourceId], exact: false });
      setSnackbarMessage('Resource updated successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    },
    onError: (error) => {
      setSnackbarMessage(error?.message || 'Failed to update resource. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    },
  });

  const reviewResourceMutation = useMutation({
    mutationKey: ['reviewResource'],
    mutationFn: ({ resourceId, reviewData }: { resourceId: number; reviewData: Partial<ResourceReview> }) =>
      reviewResource(resourceId, reviewData),
    onSuccess: (_data, variables) => {
      setIsModalOpen(false);
      setReviewDialogOpen(false);
      setReviewComment('');
      setResourceToReview(null);
      queryClient.invalidateQueries({ queryKey: ['resources'] });
      const message =
        variables.reviewData.status_id === ApprovalRequestStatus.APPROVED
          ? `Resource action has been approved`
          : `Resource action has been declined`;
      setSnackbarMessage(message);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    },
    onError: (error) => {
      setSnackbarMessage(error?.message || 'Failed to review resource. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    },
  });

  const toggleFavoriteMutation = useMutation({
    mutationKey: ['toggleFavorite'],
    mutationFn: (resourceId: number) => toggleFavorite(resourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] });
    },
    onError: (error) => {
      setSnackbarMessage(error?.message || 'Failed to toggle favorite. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    },
  });

  const deleteResourceMutation = useMutation({
    mutationKey: ['deleteResource'],
    mutationFn: (resourceId: number) => deleteResource(resourceId),
    onSuccess: () => {
      setDeleteConfirmationOpen(false);
      setResourceToDelete(null);
      queryClient.invalidateQueries({ queryKey: ['resources'] });
      setSnackbarMessage('Resource deleted successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    },
    onError: (error) => {
      setSnackbarMessage(error?.message || 'Failed to delete resource. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    },
  });

  const handleDeleteResource = (resource: Resource) => {
    setResourceToDelete(resource);
    setDeleteConfirmationOpen(true);
  };

  const handleConfirmDelete = () => {
    if (resourceToDelete !== null) {
      deleteResourceMutation.mutate(resourceToDelete.id);
    }
  };

  const { data: resourceDetails, isLoading: isLoadingResourceDetails } = useQuery({
    queryKey: ['resourceItem', fetchResourceId || editResourceId],
    queryFn: () => getResourceById((fetchResourceId || editResourceId)!),
    enabled: fetchResourceId !== null || editResourceId !== null,
  });

  if (resourceDetails && fetchResourceId !== null) {
    if (resourceDetails.download_url) {
      if (fetchAction === 'preview') {
        setPreviewUrl(resourceDetails.download_url);
        setPreviewModalOpen(true);
      } else if (fetchAction === 'download') {
        const link = document.createElement('a');
        link.href = resourceDetails.download_url;
        link.download = resourceDetails.name || 'download';
        link.click();
      }
    }
    setFetchResourceId(null);
    setFetchAction(null);
  }

  const getFavoriteParam = () => {
    if (activeTab === 1) return true;
    return undefined;
  };

  const getStatusParam = () => {
    if (activeTab === 0) return ResourceStatus.ACTIVE;
    if (activeTab === 2) return ResourceStatus.DRAFT;
    if (activeTab === 3) return ResourceStatus.DELETED;
    return undefined;
  };

  const handleAddResource = () => {
    setEditResourceId(null);
    setModalMode('add');
    setModalAction('save');
    setIsModalOpen(true);
  };

  const handleEditResource = (resource: Resource) => {
    setEditResourceId(resource.id);
    setModalMode('edit');
    setModalAction('save');
    setIsModalOpen(true);
  };

  const handleSaveResource = (resourceData: ResourceCreate, hasChanges: boolean) => {
    if (modalMode === 'add') {
      createResourceMutation.mutate(resourceData);
    } else if (editResourceId !== null && hasChanges) {
      const updateData: Partial<ResourceUpdate> = {
        name: resourceData.name,
        description: resourceData.description,
        resource_page_only: resourceData.resource_page_only,
        scope_id: resourceData.scope_id,
      };

      if (resourceData.type_id === -2 && resourceData.link) {
        updateData.link = resourceData.link;
      }

      if (resourceData.file) {
        updateData.file = resourceData.file;
      }

      if (resourceData.statutes) {
        updateData.statutes = resourceData.statutes;
      }

      updateResourceMutation.mutate({ resourceId: editResourceId, updateData });
    }
  };

  const handlePreviewResource = (resource: Resource) => {
    if (resource.type.id === ResourceType.LINK && resource.link) {
      window.open(resource.link, '_blank');
      return;
    }

    setFetchResourceId(resource.id);
    setFetchAction('preview');
  };

  const handleDownloadResource = (resource: Resource) => {
    setFetchResourceId(resource.id);
    setFetchAction('download');
  };

  const handleFavoriteToggle = (resourceId: number) => {
    toggleFavoriteMutation.mutate(resourceId);
  };

  const handleReview = (resource: Resource) => {
    setResourceToReview(resource.id);
    setEditResourceId(resource.id);
    setModalMode('edit');
    setModalAction('approve');
    setIsModalOpen(true);
  };

  const {
    data: infiniteResources,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isLoading: resourcesLoading,
  } = useInfiniteQuery({
    queryKey: ['resources', sortBy, query, activeTab],
    queryFn: async ({ pageParam = 1 }) => {
      return await getResources({
        page: pageParam,
        per_page: 30,
        sort: sortBy,
        query,
        favorite: getFavoriteParam(),
        status_id: getStatusParam(),
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<Resource[]>) => {
      if ((lastPage?.meta.pagination?.page ?? 0) < (lastPage?.meta.pagination?.pages ?? 0)) {
        return (lastPage?.meta.pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
  });

  const resources = infiniteResources?.pages?.flatMap((page) => page.data) ?? [];

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setSearchText(newSearchTerm);

    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    const delay = 500;
    timeoutId.current = setTimeout(() => {
      setQuery(newSearchTerm);
      refetch();
    }, delay);
  };

  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const pendingReview =
    resourceDetails?.approval_requests?.find((req) => req.status.id === ApprovalRequestStatus.PENDING) ||
    resourceDetails?.approval_requests?.[resourceDetails.approval_requests.length - 1];

  return (
    <Container sx={{ background: '#fff', borderRadius: 2, border: '1px solid #cbd5e0', marginY: 6 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingY: 3,
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <Typography sx={{ fontSize: '20px', fontWeight: '600' }}>Resource Hub</Typography>
        <TextField
          sx={{ width: 360 }}
          aria-label='Search'
          placeholder='Search...'
          size='small'
          value={searchText}
          onChange={handleSearchInputChange}
          slotProps={{
            input: {
              sx: { borderRadius: '8px', fontSize: '14px' },
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon size={16} />
                </InputAdornment>
              ),
            },
          }}
        />
        <Button
          variant='contained'
          color='primary'
          disableElevation
          startIcon={<PlusIcon size={18} />}
          sx={{ color: '#fff' }}
          onClick={handleAddResource}
        >
          Add New Resource
        </Button>
      </Box>

      <ResourceTable
        resources={resources}
        approvalCount={approvalCount}
        onEdit={handleEditResource}
        onPreview={handlePreviewResource}
        onDownload={handleDownloadResource}
        onFavoriteToggle={handleFavoriteToggle}
        onDelete={handleDeleteResource}
        onReview={handleReview}
        fetchNextPage={hasNextPage ? fetchNextPage : () => {}}
        loadingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isLoading={resourcesLoading}
        userInfo={userInfo}
      />

      <ResourceModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditResourceId(null);
          setModalAction('save');
        }}
        onSave={handleSaveResource}
        onApprove={() => {
          setReviewAction('approve');
          setIsModalOpen(false);
          setReviewDialogOpen(true);
        }}
        onDecline={() => {
          setReviewAction('decline');
          setIsModalOpen(false);
          setReviewDialogOpen(true);
        }}
        resource={modalMode === 'edit' ? resourceDetails : null}
        mode={modalMode}
        modalAction={modalAction}
        userInfo={userInfo}
        isLoading={modalMode === 'edit' && isLoadingResourceDetails}
        disabled={modalMode === 'edit' && resourceDetails && !resourceDetails.permissions?.can_edit}
      />

      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogContent sx={{ paddingTop: 3 }}>
          <Typography variant='h6' sx={{ fontWeight: 600, fontSize: 16, marginBottom: 1 }}>
            {reviewAction === 'approve'
              ? `Approve ${pendingReview?.action_type.description}`
              : `Decline ${pendingReview?.action_type.description}`}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder={reviewAction === 'approve' ? 'Type approval comment (optional)...' : 'Type decline reason...'}
            value={reviewComment}
            onChange={(e) => setReviewComment(e.target.value)}
            size='small'
          />
        </DialogContent>
        <DialogActions sx={{ padding: 3, paddingTop: 0 }}>
          <Button
            onClick={() => {
              setReviewDialogOpen(false);
              setReviewComment('');
            }}
            variant='outlined'
            sx={{ textTransform: 'none', minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (resourceToReview !== null) {
                reviewResourceMutation.mutate({
                  resourceId: resourceToReview,
                  reviewData: {
                    status_id:
                      reviewAction === 'approve' ? ApprovalRequestStatus.APPROVED : ApprovalRequestStatus.REJECTED,
                    ...(reviewComment.trim() && { review_comment: reviewComment }),
                  },
                });
              }
            }}
            variant='contained'
            color={reviewAction === 'approve' ? 'success' : 'error'}
            disabled={reviewAction === 'decline' && !reviewComment.trim()}
            disableElevation
            sx={{ textTransform: 'none', minWidth: 140, color: '#fff' }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <ResourceDocumentPreview
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        documentUrl={previewUrl}
      />

      <ResourceDeleteConfirmation
        open={deleteConfirmationOpen}
        onClose={() => {
          setDeleteConfirmationOpen(false);
          setResourceToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default ResourceManager;
